import { useState } from 'react';
import { User, Sun, Moon, LogOut, Camera, Trash2, Save } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../components/Toast';

export default function SettingsPage() {
  const { currentUser, updateUser, toggleTheme, theme, logout } = useApp();
  const toast = useToast();
  const [name, setName] = useState(currentUser?.name || '');
  const [imgPreview, setImgPreview] = useState(currentUser?.image || null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImgPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!name.trim()) return toast.error('Validation Error', 'Name cannot be empty.');
    updateUser({ name: name.trim(), image: imgPreview });
    toast.success('Profile Updated!', 'Your profile has been saved successfully.');
  };

  const handleRemoveImage = () => {
    setImgPreview(null);
    toast.info('Image Removed', 'Profile image has been removed.');
  };

  const initial = name?.[0]?.toUpperCase() || 'A';

  return (
    <div className="page-content">
      <div className="page-header">
        <h2>Settings</h2>
      </div>

      <div className="settings-grid">
        {/* Profile Card */}
        <div className="settings-card" style={{ gridColumn: '1 / -1' }}>
          <h3><User size={18} /> Profile Settings</h3>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 32, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div className="profile-upload">
                {imgPreview
                  ? <img src={imgPreview} alt="Profile" />
                  : <span style={{ color: '#fff', fontSize: 32, fontWeight: 800, fontFamily: 'var(--font-head)' }}>{initial}</span>
                }
                <label className="profile-upload-overlay" style={{ cursor: 'pointer' }}>
                  <Camera size={22} />
                  <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                </label>
              </div>
              {imgPreview && (
                <button className="btn btn-danger" style={{ fontSize: 12, padding: '5px 12px' }} onClick={handleRemoveImage}>
                  <Trash2 size={13} /> Remove
                </button>
              )}
            </div>

            <div style={{ flex: 1, minWidth: 240 }}>
              <div className="form-group">
                <label className="form-label">Admin Name</label>
                <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input className="form-input" value={currentUser?.email} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                <div className="form-hint">Email cannot be changed</div>
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <input className="form-input" value={currentUser?.role || 'Admin'} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
              </div>
              <button className="btn btn-primary" onClick={handleSave}>
                <Save size={15} /> Save Changes
              </button>
            </div>
          </div>
        </div>

        {/* Theme Card */}
        <div className="settings-card">
          <h3><Sun size={18} /> Appearance</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Choose your preferred display mode</p>
          <div className="theme-options">
            <div className={`theme-option${theme === 'light' ? ' active' : ''}`} onClick={() => theme !== 'light' && toggleTheme()}>
              <Sun size={20} style={{ marginBottom: 6 }} />
              <div>Light Mode</div>
            </div>
            <div className={`theme-option${theme === 'dark' ? ' active' : ''}`} onClick={() => theme !== 'dark' && toggleTheme()}>
              <Moon size={20} style={{ marginBottom: 6 }} />
              <div>Dark Mode</div>
            </div>
          </div>
        </div>

        {/* Logout Card */}
        <div className="settings-card">
          <h3><LogOut size={18} /> Session</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            You are currently signed in as <strong>{currentUser?.name}</strong>. Logging out will end your session.
          </p>
          <button className="btn btn-danger" onClick={logout} style={{ width: '100%', justifyContent: 'center' }}>
            <LogOut size={15} /> Logout from Admin Panel
          </button>
        </div>
      </div>
    </div>
  );
}
